/* oxlint-disable */
'use client';

import {
  createContext, useContext, useEffect, useState, useMemo, useCallback, useRef,
} from 'react';

import { authClient, oneTap } from '@/core/auth/client/auth-client';
import { useAuthStore } from '@/stores/userStore';

import type { Session } from '@/core/auth/client/auth-client';
import type { AuthUserDTO } from '@/stores/userStore';

interface AuthContextType {
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, remember?: boolean) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  linkGoogleAccount: () => Promise<any>;
  signOut: () => Promise<void>;
  triggerOneTap: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const toAuthUserDTO = (session: Session): AuthUserDTO => {
  const { user } = session;
  // Country is attached to the session/user at runtime by Better Auth hooks
  // (see core/auth/server/config.ts applyCountryToSession). Prefer user.country,
  // fall back to the session additional field.
  const countryFromUser = 'country' in user ? (user as AuthUserDTO).country : undefined;
  const countryFromSession = session.session?.country;

  return {
    id: user.id,
    email: user.email,
    name: user.name,
    lastName: user.lastName,
    nick: user.nick,
    phone: user.phone,
    parent: user.parent,
    emailVerified: user.emailVerified,
    createdAt: user.createdAt,
    currency: user.currency,
    country: countryFromUser ?? countryFromSession ?? 'GLOBAL',
  };
};

const throwAuthError = (error: unknown, fallback: string): never => {
  throw new Error(error instanceof Error ? error.message : fallback);
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Better Auth is the sole auth source of truth. `isSigningOut` only masks a
  // briefly-stale raw session for UI — sync/hydration always uses raw `session`.
  const { data: session, isPending: sessionLoading } = authClient.useSession();
  const [commandLoading, setCommandLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const loading = sessionLoading || commandLoading || isSigningOut;
  const effectiveSession = isSigningOut ? null : (session ?? null);

  const updateAccountFromAuthUser = useAuthStore((state) => state.updateAccountFromAuthUser);
  const clearStore = useAuthStore((state) => state.clearStore);

  const sessionRef = useRef(session);
  sessionRef.current = session;

  const lastSyncedUserIdRef = useRef<string | null | undefined>(undefined);

  const restoreAccountFromSession = useCallback((current: Session | null | undefined) => {
    if (!current?.user) {
      return
    }
    updateAccountFromAuthUser(toAuthUserDTO(current));
    lastSyncedUserIdRef.current = current.user.id;
  }, [updateAccountFromAuthUser]);

  // Raw session → Zustand business account (never effectiveSession).
  useEffect(() => {
    if (sessionLoading || isSigningOut) return;

    const nextUserId = session?.user?.id ?? null;

    if (lastSyncedUserIdRef.current === nextUserId) {
      return;
    }

    const previousUserId = lastSyncedUserIdRef.current;
    lastSyncedUserIdRef.current = nextUserId;

    if (session?.user && nextUserId) {
      updateAccountFromAuthUser(toAuthUserDTO(session));
      return;
    }

    // null after bootstrap: local/multi-tab logout, or clear stale guest persist
    const hasStaleAccount = Boolean(useAuthStore.getState().account?.id);
    if (previousUserId !== undefined || hasStaleAccount) {
      clearStore();
    }
  }, [sessionLoading, session, isSigningOut, updateAccountFromAuthUser, clearStore]);

  // Finish logout UI transition only after raw useSession() reacts to null.
  useEffect(() => {
    if (!isSigningOut || sessionLoading || session) return;

    lastSyncedUserIdRef.current = null;
    setIsSigningOut(false);
    setCommandLoading(false);
  }, [isSigningOut, session, sessionLoading]);

  const runCommand = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setCommandLoading(true);
    try {
      return await fn();
    } finally {
      setCommandLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string, remember = false) => {
    try {
      return await runCommand(async () => {
        const result = await authClient.signIn.email({
          email,
          password,
          rememberMe: remember,
        });

        if (result.error) {
          throw new Error(result.error.message || 'Login failed');
        }

        return result;
      });
    } catch (error) {
      throwAuthError(error, 'Login failed');
    }
  }, [runCommand]);

  // Do not clear flags in finally — settle effect waits for raw session → null.
  const signOut = useCallback(async () => {
    setCommandLoading(true);
    setIsSigningOut(true);
    clearStore();

    try {
      await authClient.signOut();
    } catch {
      setIsSigningOut(false);
      setCommandLoading(false);
      restoreAccountFromSession(sessionRef.current);
      throw new Error('Sign out failed');
    }
  }, [clearStore, restoreAccountFromSession]);

  const signInWithGoogle = useCallback(async () => {
    try {
      return await runCommand(async () => {
        const result = await authClient.signIn.social({
          provider: 'google',
        });

        if (result.error) {
          throw new Error(result.error.message || 'Google sign-in failed');
        }

        return result;
      });
    } catch (error) {
      throwAuthError(error, 'Google sign-in failed');
    }
  }, [runCommand]);

  const linkGoogleAccount = useCallback(async () => {
    try {
      return await runCommand(async () => {
        const result = await authClient.linkSocial({
          provider: 'google',
        });

        if (result.error) {
          throw new Error(result.error.message || 'Google account linking failed');
        }

        return result;
      });
    } catch (error) {
      throwAuthError(error, 'Google account linking failed');
    }
  }, [runCommand]);

  const triggerOneTap = useCallback(async () => {
    try {
      await oneTap();
      return { success: true };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'One Tap failed' };
    }
  }, []);

  const value = useMemo(() => ({
    session: effectiveSession,
    loading,
    signIn,
    signInWithGoogle,
    linkGoogleAccount,
    signOut,
    triggerOneTap,
  }), [effectiveSession, loading, signIn, signInWithGoogle, linkGoogleAccount, signOut, triggerOneTap]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
